import React from 'react';
import { useBooking } from '../../contexts/booking/BookingContext';
import { useAuth } from '../../contexts/auth/AuthContext';
import { getEligibleCredits, formatPrice } from '../../utils/bookingUtils';

/**
 * CreditSelectionSection
 * Shows available free service credits for services in the cart
 * Allows members to toggle credit usage for eligible services
 */
const CreditSelectionSection: React.FC = () => {
  const { bookingState, toggleCreditForService } = useBooking();
  const { memberCredits, isMember } = useAuth();

  // Only show for members with credits
  if (!isMember || memberCredits.length === 0) {
    return null;
  }

  // Get credits that match services in cart
  const eligibleCredits = getEligibleCredits(bookingState.selectedServices, memberCredits);

  // No eligible credits for services in cart
  if (eligibleCredits.length === 0) {
    return null;
  }

  // Get service prices for display
  const getServicePrice = (serviceId: string): number => {
    const service = bookingState.selectedServices.find(s => s.id === serviceId);
    return service?.numericPrice || 0;
  };

  // Count how many of a service are in the cart
  const getServiceCountInCart = (serviceId: string): number => {
    return bookingState.selectedServices.filter(s => s.id === serviceId).length;
  };

  // Check if credit is currently being used
  const isCreditUsed = (serviceId: string): boolean => {
    return bookingState.creditsToUse.some(c => c.serviceId === serviceId);
  };

  return (
    <div className="bg-gradient-to-r from-teal-50 to-amber-50 rounded-xl p-4 mb-4 border border-teal-200">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="font-semibold text-gray-800">Free Service Credits</h3>
      </div>
      <p className="text-sm text-gray-600 mb-3">
        As a VIP member, you have free service credits! Toggle to apply them to your booking.
      </p>

      <div className="space-y-2">
        {eligibleCredits.map(credit => {
          const isUsed = isCreditUsed(credit.serviceId);
          const countInCart = getServiceCountInCart(credit.serviceId);
          const applicableQuantity = Math.min(credit.quantity, countInCart);
          const pricePerService = getServicePrice(credit.serviceId);
          const totalSavings = pricePerService * applicableQuantity;

          return (
            <div
              key={credit.serviceId}
              className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                isUsed ? 'bg-teal-100 border-2 border-teal-500' : 'bg-white border border-gray-200'
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-800">{credit.serviceName}</span>
                  {isUsed && (
                    <span className="px-2 py-0.5 bg-teal-500 text-white text-xs font-bold rounded">
                      FREE
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {credit.quantity} credit{credit.quantity !== 1 ? 's' : ''} available
                  {countInCart > 1 && ` (${countInCart} in cart)`}
                </div>
                {isUsed && (
                  <div className="text-sm text-teal-600 font-medium">
                    Saving ${formatPrice(totalSavings)}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => toggleCreditForService(credit.serviceId, credit.quantity)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                  isUsed ? 'bg-teal-500' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={isUsed}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isUsed ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>

      {bookingState.creditsToUse.length > 0 && (
        <div className="mt-3 pt-3 border-t border-teal-200">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Credits applied:</span>
            <span className="font-semibold text-teal-600">
              {bookingState.creditsToUse.reduce((sum, c) => sum + c.quantity, 0)} service{bookingState.creditsToUse.reduce((sum, c) => sum + c.quantity, 0) !== 1 ? 's' : ''} free
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditSelectionSection;
